import { mlServer, type MediaLibraryMessage, useMediaLibraryLanguage, mlFeedback, mlMessage } from "../i18n/mediaLibraryPresentation";

/**
 * useAmcCapabilities
 *
 * Fetches the per-user Asset Management Center capability contract from
 * `GET /api/content/capabilities`. The page gates tab visibility on
 * `allowedTabs` and branches admin-only affordances on the individual flags
 * (never on a hardcoded role). The backend enforces the same rules server-side.
 */
import { useCallback, useEffect, useRef, useState } from "react";

/** Mirrors the backend `AmcTab` union (mediaCenterService.ts). */
export type AmcTab = "local" | "brand" | "app-logos" | "avatars" | "backups";

/** Mirrors the backend `AmcCapabilities` contract (mediaCenterService.ts). */
export interface AmcCapabilities {
  canAccess: boolean;
  isAdmin: boolean;
  canManageBranding: boolean;
  canManageLocalAssets: boolean;
  canManagePersonalAssets: boolean;
  canManageAppLogos: boolean;
  canSeeAllAvatars: boolean;
  canResetAvatars: boolean;
  canViewAppBackups: boolean;
  allowedTabs: AmcTab[];
}

type AmcCapabilitiesState = {
  capabilities: AmcCapabilities | null;
  loading: boolean;
  error: string | MediaLibraryMessage | null;
};

export const useAmcCapabilities = () => {
  useMediaLibraryLanguage();
  const [state, setState] = useState<AmcCapabilitiesState>({
    capabilities: null,
    loading: true,
    error: null,
  });
  const abortRef = useRef<AbortController | null>(null);

  const fetchCapabilities = useCallback(async (signal?: AbortSignal) => {
    try {
      const res = await fetch("/api/content/capabilities", {
        credentials: "include",
        signal,
      });
      const json = (await res.json().catch(() => null)) as
        | { ok: true; data: { capabilities: AmcCapabilities } }
        | { ok: false; message?: string }
        | null;

      if (!res.ok || !json?.ok) {
        const message =
          (json && "message" in json && json.message) || `HTTP ${res.status}`;
        setState({ capabilities: null, loading: false, error: mlServer(json, message) });
        return;
      }
      setState({ capabilities: json.data.capabilities, loading: false, error: null });
    } catch (err) {
      if (signal?.aborted) return;
      setState({
        capabilities: null,
        loading: false,
        error: mlFeedback(err, "failedToLoadCapabilities"),
      });
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;
    void fetchCapabilities(controller.signal);
    return () => controller.abort();
  }, [fetchCapabilities]);

  return { ...state, error: mlMessage(state.error) };
};
